// PickupVerifyDialog — small focused step when a teacher confirms a
// non-guardian is collecting a child:
//   • If the authorised person has a PIN → show 4-digit entry; on
//     match, stamp the pickup.
//   • If no PIN → show a single "Confirm pickup" CTA.
//
// Calm, no-ceremony. ESC dismisses.

import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ShieldCheck, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { stampPickup, todayStr } from "@/lib/attendance";
import PickupPhoto from "@/components/pickup/PickupPhoto";

export default function PickupVerifyDialog({
  open,
  onOpenChange,
  child,            // {id, first_name, last_name}
  person,           // pickup_authorization row, or null
  onCompleted,
}) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPin("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  if (!person) return null;

  const requiresPin = !!person.pin;
  const childName =
    [child?.first_name, child?.last_name].filter(Boolean).join(" ") || "Child";

  const onConfirm = async () => {
    if (requiresPin) {
      if (pin.length !== 4) {
        toast.error("Enter the 4-digit PIN");
        return;
      }
      if (pin !== person.pin) {
        toast.error("PIN doesn't match. Ask the parent.");
        setPin("");
        inputRef.current?.focus();
        return;
      }
    } else if (
      !window.confirm(
        `Confirm ${childName} is being picked up by ${person.full_name}?`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await stampPickup({
        childId: child.id,
        date: todayStr(),
        pickupAuthorizationId: person.id,
        pickupSelf: false,
      });
      toast.success(`Pickup confirmed · ${person.full_name}`);
      onCompleted?.(person);
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err.message || "Could not stamp pickup");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="pickup-verify-dialog"
        className="sm:max-w-sm"
      >
        <DialogHeader className="text-left">
          <DialogTitle className="font-heading text-lg">
            {requiresPin ? "Enter PIN" : "Confirm pickup"}
          </DialogTitle>
          <DialogDescription>
            {requiresPin
              ? "Ask the person collecting the child for their 4-digit PIN."
              : "Tap to record this person collecting the child."}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 flex items-center gap-3 rounded-2xl border border-neutral-200 bg-neutral-50/40 p-3">
          <PickupPhoto path={person.photo_path} name={person.full_name} size={48} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-neutral-900">
              {person.full_name}
            </p>
            <p className="text-[12px] text-neutral-600">
              {person.relationship || "—"}
              {person.phone ? ` · ${person.phone}` : ""}
            </p>
            <p className="mt-0.5 text-[11px] text-neutral-500">
              Collecting <strong>{childName}</strong>
            </p>
          </div>
        </div>

        {requiresPin && (
          <label className="mt-4 block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">
              4-digit PIN
            </span>
            <div className="relative">
              <ShieldCheck
                size={14}
                weight="duotone"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                aria-hidden
              />
              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                pattern="\d{4}"
                maxLength={4}
                autoComplete="off"
                data-testid="pickup-verify-pin"
                value={pin}
                onChange={(e) =>
                  setPin(e.target.value.replace(/\D/g, "").slice(0, 4))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" && pin.length === 4) onConfirm();
                }}
                placeholder="• • • •"
                className="w-full rounded-xl border border-neutral-200 bg-white py-2 pl-9 pr-3 text-center text-lg tracking-[0.5em] text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </label>
        )}

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            data-testid="pickup-verify-cancel"
            onClick={() => onOpenChange?.(false)}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            <X size={12} weight="bold" />
            Cancel
          </button>
          <button
            type="button"
            data-testid="pickup-verify-confirm"
            disabled={busy || (requiresPin && pin.length !== 4)}
            onClick={onConfirm}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
          >
            {busy
              ? "Confirming…"
              : requiresPin
                ? "Verify & confirm"
                : "Confirm pickup"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
